import { getBaseUrl, promiseRequest } from "./ServiceApi";
import User from '../model/User';
import Lock from "../utils/Lock";
import { PermissionError } from "./Errors";

const localStorageKey = "safedesk_auth_tokens";

function saveAuthToken({ access_token, refresh_token }: AuthResponse) {
	return localStorage.setItem(localStorageKey, JSON.stringify({ access_token, refresh_token }));
};

function clearAuthToken() {
	return localStorage.removeItem(localStorageKey);
};

function getAuthToken(): AuthResponse|null {
	var token = localStorage.getItem(localStorageKey);
	return typeof token === "string" ? JSON.parse(token) : null;
};

export interface AuthResponse {
	access_token: string;
	refresh_token: string;
}

class LoginRequest {

	constructor(public login: string, public password: string) {
		this.login = login;
		this.password = password;
	}
}

class RefreshRequest {
	constructor(public refresh_token: string) {
		this.refresh_token = refresh_token;
	}
}

let _instance: AuthService;

export default class AuthService {

	static get instance() {
		if (_instance == null) {
			_instance = new AuthService();
		}
		return _instance;
	}

	private lock: Lock;

	constructor() {
		this.lock = new Lock();
		this.login = this.login.bind(this);
		this.logout = this.logout.bind(this);
		this.getAccessToken = this.getAccessToken.bind(this);
		this.getRefreshToken = this.getRefreshToken.bind(this);
	}

	getAccessToken() {
		var data = getAuthToken();
		return data !== null ? data.access_token : null;
	}

	getRefreshToken() {
		var data = getAuthToken();
		return data !== null ? data.refresh_token : null;
	}

	async refreshToken() {
		await this.lock.acquire();
		var refresh_token = this.getRefreshToken();
		if (!refresh_token) {
			this.lock.release();
			return Promise.resolve(null);
		}
		var url = getBaseUrl('auth') + 'refreshToken/';
		var request = new RefreshRequest(refresh_token);
		var options = {
			method: "POST",
			headers: {
				"Accept": "application/json",
				"Content-Type": "application/json"
			},
			body: JSON.stringify(request)
		};
		var token: AuthResponse | null = null;
		const response = await fetch(url, options);
		if (response.ok) {
			token = await response.json();
			if (token) {
				saveAuthToken(token);
			}
		}
		this.lock.release();
		return token;
	}

	async login(email: string, password: string): Promise<boolean> {
		var request = new LoginRequest(email, password);
		var options = {
			method: "POST",
			headers: {
				"Accept": "application/json",
				"Content-Type": "application/json"
			},
			body: JSON.stringify(request)
		};
		var token: AuthResponse;
		var url = getBaseUrl('auth') + 'login/';
		const response = await fetch(url, options);
		if (response.ok) {
			token = await response.json();
			saveAuthToken(token);
			return true;
		} else {
			clearAuthToken();
			const text = await response.text();
			if (response.status === 403) {
				const r = JSON.parse(text);
				throw new PermissionError(r && r.message);
			}
			throw new Error("Login Inválido");
		}
	}

	async logout() {
		var tokens = getAuthToken();
		if (tokens) {
			var item = new RefreshRequest(tokens.refresh_token);
			var options = {
				method: "POST",
				headers: {
					"Accept": "application/json",
					"Content-Type": "application/json"
				},
				body: JSON.stringify(item)
			};
			var url = getBaseUrl('auth') + 'logout/';
			try {
				await fetch(url, options);
			} catch (e) {
				console.error(e);
			}
		}
		clearAuthToken();
	}

	getAuthUser(): Promise<User> {
		var url = getBaseUrl('auth') + 'user';
		return promiseRequest(url);
	}

}
